import { getAppCopy } from '../../../i18n';
import type { EntitlementStatus } from '../types';
import { PurchaseCancelledError } from './appStore';

type MacIapBridge = {
  isAvailable: () => Promise<boolean>;
  getEntitlement: () => Promise<{
    tier?: 'free' | 'pro';
    source?: EntitlementStatus['source'];
    formattedPrice?: string;
    originalTransactionId?: string;
  }>;
  purchase: () => Promise<{
    tier?: 'free' | 'pro';
    source?: EntitlementStatus['source'];
    formattedPrice?: string;
  }>;
  restore: () => Promise<boolean>;
  manageSubscriptions: () => Promise<void>;
  getSubscriptionForRedeem: () => Promise<{ originalTransactionId: string } | null>;
};

function getMacIap(): MacIapBridge | null {
  const iap = (window as { electron?: { iap?: MacIapBridge } }).electron?.iap;
  return iap ?? null;
}

export function isMacAppStorePurchasePlatform(): boolean {
  return Boolean(getMacIap());
}

function mapEntitlement(data: {
  tier?: 'free' | 'pro';
  source?: EntitlementStatus['source'];
  formattedPrice?: string;
}): EntitlementStatus {
  return {
    tier: data.tier === 'pro' ? 'pro' : 'free',
    source: 'appstore',
    formattedPrice: data.formattedPrice,
  };
}

export async function fetchMacAppStoreEntitlement(): Promise<EntitlementStatus> {
  const iap = getMacIap();
  if (!iap) {
    throw new Error(getAppCopy().billingErrors.purchaseUnavailable);
  }
  const available = await iap.isAvailable();
  if (!available) {
    throw new Error(getAppCopy().billingErrors.purchaseUnavailable);
  }
  return mapEntitlement(await iap.getEntitlement());
}

export async function purchaseMacAppStore(): Promise<void> {
  const iap = getMacIap();
  if (!iap) {
    throw new Error(getAppCopy().billingErrors.purchaseUnavailable);
  }
  try {
    const result = await iap.purchase();
    if (result.tier !== 'pro') {
      throw new Error(getAppCopy().billingErrors.purchaseUnavailable);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : '';
    const code = (error as { code?: string }).code;
    if (code === 'cancelled' || message === 'cancelled') {
      throw new PurchaseCancelledError();
    }
    throw error;
  }
}

export async function restoreMacAppStore(): Promise<boolean> {
  const iap = getMacIap();
  if (!iap) return false;
  return iap.restore();
}

export async function manageMacAppStoreSubscription(): Promise<void> {
  const iap = getMacIap();
  if (!iap) {
    throw new Error(getAppCopy().billingErrors.manageUnavailable);
  }
  await iap.manageSubscriptions();
}

export async function getMacAppStoreSubscriptionForRedeem(): Promise<{
  originalTransactionId: string;
} | null> {
  const iap = getMacIap();
  if (!iap) return null;
  return iap.getSubscriptionForRedeem();
}
